import "@hotwired/turbo-rails"
import Alpine from "alpinejs"
import sort from "@alpinejs/sort"
import { applyTheme, getTheme, initThemeToggles } from "./theme"
import { initScrollReveal } from "./scroll_reveal"
import { registerCommandPalette } from "./command_palette"

// Powers touch-and-mouse drag-reorder (e.g. Pages' Build tab block list) —
// replaces the earlier hand-rolled native HTML5 drag-and-drop, which had no
// touch support at all on mobile.
Alpine.plugin(sort)

// ⌘K command palette + global search (user + admin panels)
registerCommandPalette(Alpine)

window.Alpine = Alpine
Alpine.start()

// Apply theme on every Turbo navigation (class is set inline before load, this keeps buttons in sync)
document.addEventListener("turbo:load", () => {
  applyTheme(getTheme())
  initThemeToggles()
  initScrollReveal()
})

// Remove flash messages from Turbo's page cache snapshot so they never
// reappear when the user hits back after a sign-in redirect.
document.addEventListener("turbo:before-cache", () => {
  document.querySelectorAll("[data-flash-message], [data-controller='flash'], [data-flash-container]").forEach(el => el.remove())
})

document.addEventListener("turbo:load", () => {
  document.querySelectorAll("[data-user-menu]").forEach((menu) => {
    const trigger = menu.querySelector("[data-user-menu-trigger]")
    const panel = menu.querySelector("[data-user-menu-panel]")
    if (!trigger || !panel) return

    let closeTimeout

    const openMenu = () => {
      if (closeTimeout) clearTimeout(closeTimeout)
      panel.classList.remove("hidden")
      trigger.setAttribute("aria-expanded", "true")
    }

    const closeMenu = () => {
      closeTimeout = setTimeout(() => {
        panel.classList.add("hidden")
        trigger.setAttribute("aria-expanded", "false")
      }, 220)
    }

    menu.addEventListener("mouseenter", openMenu)
    menu.addEventListener("mouseleave", closeMenu)
    menu.addEventListener("focusin", openMenu)
    menu.addEventListener("focusout", (event) => {
      if (!menu.contains(event.relatedTarget)) closeMenu()
    })
  })
})

document.addEventListener("click", (event) => {
  const dismissButton = event.target.closest("[data-flash-dismiss], [data-action~='flash#dismiss'], [data-action~='click->flash#dismiss']")
  if (!dismissButton) return

  const flashMessage = dismissButton.closest("[data-flash-message], [data-controller='flash']")
  if (!flashMessage) return

  const flashContainer = flashMessage.parentElement
  flashMessage.remove()

  if (flashContainer?.matches("[data-flash-container]") && !flashContainer.querySelector("[data-flash-message], [data-controller='flash']")) {
    flashContainer.remove()
  }
})

const copyToClipboard = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text)
    return
  }

  const textArea = document.createElement("textarea")
  textArea.value = text
  textArea.setAttribute("readonly", "")
  textArea.style.position = "absolute"
  textArea.style.left = "-9999px"
  document.body.appendChild(textArea)
  textArea.select()
  document.execCommand("copy")
  document.body.removeChild(textArea)
}

const showCopyToast = (message = "Copied to clipboard!") => {
  document.getElementById("copy-toast")?.remove()

  const toast = document.createElement("div")
  toast.id = "copy-toast"
  toast.textContent = message
  Object.assign(toast.style, {
    position: "fixed",
    bottom: "24px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: "9999",
    background: "#111827",
    color: "#fff",
    fontSize: "0.875rem",
    fontWeight: "500",
    padding: "8px 18px",
    borderRadius: "8px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    pointerEvents: "none",
    transition: "opacity 0.2s ease",
    opacity: "0",
  })

  document.body.appendChild(toast)
  requestAnimationFrame(() => {
    toast.style.opacity = "1"
    setTimeout(() => {
      toast.style.opacity = "0"
      setTimeout(() => toast.remove(), 220)
    }, 1800)
  })
}

const applyCopyFeedback = (button, origHTML) => {
  const svgEl = button.querySelector("svg")
  const svgClasses = svgEl?.getAttribute("class") || "w-4 h-4"
  const hasLabel = button.textContent.trim().length > 0
  const checkmark = `<svg class="${svgClasses}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>`

  button.innerHTML = hasLabel ? `${checkmark} Copied!` : checkmark
  button.style.color = "rgb(16, 185, 129)"

  showCopyToast()

  setTimeout(() => {
    button.innerHTML = origHTML
    button.style.color = ""
  }, 2000)
}

window.showCopyToast = showCopyToast
window.copyToClipboard = copyToClipboard

// Copies the text content of a <pre id="..."> code block — used by the API
// reference explorer (Developer Settings and the public /developers page).
window.copyCodeBlock = async (id, btn) => {
  const el = document.getElementById(id)
  if (!el) return

  try {
    await copyToClipboard(el.innerText)
    applyCopyFeedback(btn, btn.innerHTML)
  } catch (error) {
    console.error("Copy failed:", error)
  }
}

const generateShareToken = () => {
  const bytes = new Uint8Array(9)
  crypto.getRandomValues(bytes)
  return btoa(String.fromCharCode(...bytes)).replace(/[+/=]/g, "").slice(0, 12)
}

const appendShareToken = (url) => {
  try {
    const u = new URL(url)
    u.searchParams.set("_st", generateShareToken())
    return u.toString()
  } catch {
    // If URL is relative or unparseable, append manually
    const sep = url.includes("?") ? "&" : "?"
    return `${url}${sep}_st=${generateShareToken()}`
  }
}

const initializeLinksPageInteractions = () => {
  const linksWrapper = document.body

  if (linksWrapper && !linksWrapper.dataset.linksInteractionsBound) {
    linksWrapper.addEventListener("click", async (event) => {
      const copyButton = event.target.closest("[data-copy-link]")
      if (!copyButton) return

      const link = copyButton.dataset.copyLink
      if (!link) return

      try {
        // Append a share token so clicks from this copy are traceable as dark social
        const trackedLink = appendShareToken(link)
        await copyToClipboard(trackedLink)
        const origHTML = copyButton.innerHTML
        applyCopyFeedback(copyButton, origHTML)
      } catch (error) {
        console.error("Copy failed:", error)
      }
    })

    linksWrapper.dataset.linksInteractionsBound = "true"
  }
}

document.addEventListener("turbo:load", initializeLinksPageInteractions)

// Confirm-action modal — delegated listeners bound ONCE at script-load time,
// not re-bound on turbo:load. #confirm-action-modal lives in the layout, so
// Turbo Drive recreates it as a fresh DOM node on every navigation; a guard
// keyed on that node (the previous approach) never actually holds, so a full
// new listener set got added to `document` on every single page visit,
// forever, with nothing ever removing the old ones. The stale listeners then
// raced the real one on every subsequent submit — one of them would clear
// the just-set `confirmed` flag before the real listener read it, which
// re-triggered preventDefault() and silently swallowed the resubmission,
// leaving the button disabled ("Please wait…") forever since the request
// never actually reached the server. Looking up the modal element fresh
// inside each handler (instead of caching it at bind time) makes this safe
// regardless of how many Turbo navigations have happened.
;(() => {
  let pendingAction = null

  const modalEl = () => document.getElementById("confirm-action-modal")

  const hideModal = () => {
    modalEl()?.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    pendingAction = null
  }

  const showModal = ({ title, message, confirmLabel, danger, action }) => {
    const el = modalEl()
    if (!el) return
    const titleElement = el.querySelector("[data-confirm-modal-title]")
    const messageElement = el.querySelector("[data-confirm-modal-message]")
    const confirmButton = el.querySelector("[data-confirm-modal-confirm]")
    const dangerIcon = el.querySelector("[data-confirm-modal-icon-danger]")
    const warningIcon = el.querySelector("[data-confirm-modal-icon-warning]")
    if (titleElement) titleElement.textContent = title
    if (messageElement) messageElement.textContent = message
    if (confirmButton) {
      confirmButton.textContent = confirmLabel
      confirmButton.classList.toggle("bg-red-600", danger)
      confirmButton.classList.toggle("hover:bg-red-700", danger)
      confirmButton.classList.toggle("bg-amber-500", !danger)
      confirmButton.classList.toggle("hover:bg-amber-600", !danger)
    }
    dangerIcon?.classList.toggle("hidden", !danger)
    dangerIcon?.classList.toggle("flex", danger)
    warningIcon?.classList.toggle("hidden", danger)
    warningIcon?.classList.toggle("flex", !danger)
    pendingAction = action
    el.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    el.querySelector("[data-confirm-modal-cancel]")?.focus()
  }

  document.addEventListener("submit", (event) => {
    const form = event.target.closest("form[data-confirm-modal-message]")
    if (!form) return
    if (form.dataset.confirmed === "true") {
      delete form.dataset.confirmed
      return
    }

    event.preventDefault()
    // DELETE forms (button_to method: :delete renders a hidden _method field)
    // get the destructive red treatment; everything else the amber warning one.
    const isDelete = form.querySelector('input[name="_method"][value="delete"]') !== null
    showModal({
      title: form.dataset.confirmModalTitle || "Please confirm this action",
      message: form.dataset.confirmModalMessage,
      confirmLabel: form.dataset.confirmModalConfirmLabel || "Confirm",
      danger: isDelete,
      action: () => {
        form.dataset.confirmed = "true"
        form.requestSubmit()
      }
    })
  })

  // Links submitted through Turbo (data-turbo-method) can't carry the modal
  // attrs on a form, so intercept the click in the CAPTURE phase — before
  // Turbo's own bubbling click listener builds and submits its hidden form.
  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[data-confirm-modal-message][data-turbo-method]")
    if (!link) return
    if (link.dataset.confirmed === "true") {
      delete link.dataset.confirmed
      return
    }

    event.preventDefault()
    event.stopPropagation()
    showModal({
      title: link.dataset.confirmModalTitle || "Please confirm this action",
      message: link.dataset.confirmModalMessage,
      confirmLabel: link.dataset.confirmModalConfirmLabel || "Confirm",
      danger: link.dataset.turboMethod === "delete",
      action: () => {
        link.dataset.confirmed = "true"
        link.click()
      }
    })
  }, true)

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-confirm-modal-confirm]")) {
      if (pendingAction) pendingAction()
      hideModal()
    } else if (event.target.closest("[data-confirm-modal-cancel], [data-confirm-modal-close]")) {
      hideModal()
    }
  })

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !modalEl()?.classList.contains("hidden")) hideModal()
  })
})()

// Safety scan Retry/Recheck button — toast feedback + 15-second cooldown per link
// Cooldown persists in localStorage so it survives Turbo Stream DOM replacement.
;(function () {
  const COOLDOWN_MS = 15_000
  const STORAGE_KEY = "safety_recheck_cd"

  function readCooldowns() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}") } catch { return {} }
  }

  function setCooldown(id) {
    const data = readCooldowns()
    data[id] = Date.now()
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  }

  function cooldownRemaining(id) {
    const ts = readCooldowns()[id]
    if (!ts) return 0
    return Math.max(0, COOLDOWN_MS - (Date.now() - ts))
  }

  document.addEventListener("click", (event) => {
    const btn = event.target.closest("[data-recheck-btn]")
    if (!btn) return

    const id = btn.dataset.recheckBtn
    const remaining = cooldownRemaining(id)

    if (remaining > 0) {
      event.preventDefault()
      const secs = Math.ceil(remaining / 1000)
      showCopyToast(`Please wait ${secs}s before rechecking again`)
      return
    }

    setCooldown(id)
    showCopyToast("Safety scan queued!")
  })
})()

// Disable submit buttons on form submission, re-enable on error so user can retry
document.addEventListener("submit", (event) => {
  const form = event.target
  if (!(form instanceof HTMLFormElement)) return
  // Skip if the confirm modal hasn't been accepted yet (will re-submit)
  if (form.dataset.confirmModalMessage && form.dataset.confirmed !== "true") return

  const buttons = form.querySelectorAll("input[type='submit'], button[type='submit'], button:not([type])")
  buttons.forEach((btn) => {
    btn.disabled = true
    btn.dataset.originalText = btn.value || btn.textContent
    if (btn.tagName === "INPUT") {
      btn.value = "Please wait…"
    } else {
      btn.textContent = "Please wait…"
    }
  })
})

// Re-enable buttons if the server responded with an error (4xx/5xx) or a Turbo form redirect
document.addEventListener("turbo:submit-end", (event) => {
  const form = event.detail?.formSubmission?.formElement
  if (!form) return
  const buttons = form.querySelectorAll("input[type='submit'], button[type='submit'], button:not([type])")
  buttons.forEach((btn) => {
    btn.disabled = false
    if (btn.dataset.originalText) {
      if (btn.tagName === "INPUT") {
        btn.value = btn.dataset.originalText
      } else {
        btn.textContent = btn.dataset.originalText
      }
      delete btn.dataset.originalText
    }
  })
})
